/**
 * Connector lifecycle status (enum-compatible const object).
 */
export const ConnectorStatus = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  ERROR: 'ERROR',
  EXPIRED: 'EXPIRED',
} as const

export type ConnectorStatus = (typeof ConnectorStatus)[keyof typeof ConnectorStatus]

export type ConnectorId =
  | 'pauta'
  | 'agis'
  | 'all-nations'
  | 'ingram'
  | 'oderco'
  | 'snd'
  | 'kabum'
  | 'terabyte'
  | 'pichau'
  | 'amazon'
  | 'mercado-livre'
  | 'magazine-luiza'
  | (string & {})

export interface ConnectorProductRef {
  sku?: string
  ean?: string
  mpn?: string
  query?: string
  brand?: string
}

export interface ConnectorProductResult {
  sku: string
  name: string
  brand?: string
  /** Prices are optional until Price Engine wiring — SDK returns shape only. */
  price?: number | null
  currency?: string
  stock?: number | null
  url?: string
  supplier: string
  raw?: Record<string, unknown>
}

export interface ConnectorStockResult {
  sku: string
  available: boolean
  quantity: number | null
  warehouse?: string
  updatedAt?: string
}

export interface ConnectorPriceResult {
  sku: string
  price: number | null
  currency: string
  updatedAt?: string
}

export interface ConnectorHealthResult {
  status: ConnectorStatus
  healthy: boolean
  checkedAt: string
  latencyMs?: number
  message?: string
  details?: Record<string, unknown>
}

export interface ConnectorConnectOptions {
  integrationId?: string
  sessionId?: string
  force?: boolean
}

export interface ConnectorSearchOptions {
  limit?: number
  page?: number
  signal?: AbortSignal
}

export interface ConnectorMeta {
  id: ConnectorId
  name: string
  version: string
  integrationId?: string
}

export interface IConnector {
  readonly meta: ConnectorMeta
  readonly capabilities: readonly import('./ConnectorCapabilities').ConnectorCapability[]

  getStatus(): ConnectorStatus

  connect(options?: ConnectorConnectOptions): Promise<void>
  disconnect(): Promise<void>
  testConnection(): Promise<boolean>
  searchProduct(ref: ConnectorProductRef, options?: ConnectorSearchOptions): Promise<ConnectorProductResult | null>
  searchProducts(query: string, options?: ConnectorSearchOptions): Promise<ConnectorProductResult[]>
  getStock(sku: string): Promise<ConnectorStockResult>
  getPrice(sku: string): Promise<ConnectorPriceResult>
  refreshSession(): Promise<void>
  health(): Promise<ConnectorHealthResult>
}
